import styled from 'styled-components';

// The card is the only occupant of the dock body while a node is
// selected, so it grows to fill the panel; the transcript scrolls
// inside it. When collapsed it hugs just the header row.
export const LogCard = styled.div<{ $collapsed?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 14px 14px;
  background: ${({ theme: { colors = {} } }) => colors.cardSurface ?? '#15151F'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  border-radius: 24px;
  flex: ${({ $collapsed }) => ($collapsed ? '0 0 auto' : '1 1 auto')};
  min-height: ${({ $collapsed }) => ($collapsed ? 'auto' : '220px')};
  overflow: hidden;
`;

export const LogHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

// Container for title + spinner, fills the available row width so the
// chevron toggle is pinned to the far right.
export const LogHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

export const LogTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
`;

/**
 * Chevron toggle to collapse/expand the log body. Renders as a borderless
 * icon button so it doesn't compete visually with the title; size matches
 * the design's affordance density.
 */
export const CollapseToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;

  &:hover {
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainerHigh ?? '#1F2030'};
    color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme: { colors = {} } }) => colors.primary ?? '#7B68EE'};
    outline-offset: 2px;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

// Bounded scroll wrapper for the error MessageBox so a long failure
// message (e.g. a full provider error dump that survives sanitization)
// can't overflow the LogCard horizontally and clip on the right edge
// (regression: the Auto Gate Agent Log used to bleed off-screen with
// a 422 dump). We cap the height at 192px and let the user scroll
// vertically within the card; word-break keeps any long unbroken
// tokens from forcing horizontal overflow.
export const ErrorScroll = styled.div`
  max-height: 192px;
  overflow-y: auto;
  overflow-x: hidden;
  word-break: break-word;
  flex-shrink: 0;

  & > * {
    width: 100%;
    box-sizing: border-box;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme: { colors = {} } }) =>
      colors.outlineVariant ?? '#49454F'};
    border-radius: 2px;
  }
`;

// The raw executor text behind "View technical details" is unbounded
// (2000 chars at the DB ceiling), so it scrolls inside the disclosure
// rather than pushing the transcript out of the card.
export const DetailScroll = styled.div`
  max-height: 160px;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme: { colors = {} } }) =>
      colors.outlineVariant ?? '#49454F'};
    border-radius: 2px;
  }
`;

// Internal scroller for the transcript. The log is capped on the
// outer {@link LogCard}; this gives the body its own overflow so a
// 500-message run doesn't push the rest of the sidebar (ExecutionLog,
// footer buttons) off the bottom. The sidebar's {@code PanelBody} still
// scrolls the panel as a unit when the OTHER cards (RunHistory, etc.)
// don't fit — this only owns scrolling INSIDE the agent log.
export const TranscriptScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme: { colors = {} } }) =>
      colors.outlineVariant ?? '#49454F'};
    border-radius: 2px;
  }
`;

// Inner content wrapper observed by a {@code ResizeObserver} so the
// auto-scroll-on-grow effect catches the case where streamed chunks
// accumulate into the LAST coalesced text block (which doesn't change
// items.length and would otherwise be missed by a React-dep effect).
export const TranscriptInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const TextBlock = styled.div`
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 11px;
  line-height: 1.6;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  white-space: pre-wrap;
  word-break: break-word;
`;

// Block wrapper so the chat-style tool chip spans the card width.
export const ToolRow = styled.div`
  display: block;
`;

